import type { KidsPolicyItemInput } from "@/lib/gql/__generated__/graphql";
import { KidsPolicyType } from "@/lib/gql/__generated__/graphql";

import { FormLabel } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { QuestionSectionWrapper } from "../../../system/question/_layouts/item-wrapper";
import { QuestionCardItem } from "../../../system/question/card/card-item";

import type { StatusesGenerator } from "../../../../../types/edit-statuses";

type Props = {
	kidsPolicyItem: StatusesGenerator<KidsPolicyItemInput, "">;

	canRemoveKidsPolicyItem: ({ index }: { index: number }) => boolean;
	removeKidsPolicyItemByIndex: ({ index }: { index: number }) => void;

	kidsPolicyKidsMaxAgeMinAge: ({ index }: { index: number }) => number;
	kidsPolicyKidsMaxAgeOptions: ({ index }: { index: number }) => FormOptionType[];
	changeKidsPolicyMaxAge: ({ maxAge, index }: { maxAge: number; index: number }) => void;

	discountTypeOptions: FormOptionType[];
	changeKidsPolicyDiscountType: ({ discountType, index }: { discountType: KidsPolicyType; index: number }) => void;

	kidsPolicyRatioOptions: FormOptionType[];
	changeKidsPolicyRatio: ({ ratio, index }: { ratio: number; index: number }) => void;

	changeKidsPolicyStaticPrice: ({ staticPrice, index }: { staticPrice: number | undefined; index: number }) => void;

	priceTypeLabel?: string;

	index: number;
};

export const QuestionKidsPolicyItem = ({
	kidsPolicyItem,
	canRemoveKidsPolicyItem,
	removeKidsPolicyItemByIndex,
	kidsPolicyKidsMaxAgeMinAge,
	kidsPolicyKidsMaxAgeOptions,
	changeKidsPolicyMaxAge,
	changeKidsPolicyDiscountType,
	kidsPolicyRatioOptions,
	changeKidsPolicyRatio,
	discountTypeOptions,
	changeKidsPolicyStaticPrice,
	priceTypeLabel = "部屋料金",
	index,
}: Props) => {
	const kidsDiscountType = kidsPolicyItem.kidsDiscountType.value;

	const showRatio = kidsDiscountType === KidsPolicyType.Ratio;
	const showStatic = kidsDiscountType === KidsPolicyType.Static;

	const remove = (index: number) => () => {
		removeKidsPolicyItemByIndex({ index });
	};

	const updateKidsMaxAge = (e: string) => {
		changeKidsPolicyMaxAge({ index, maxAge: Number(e) });
	};

	const updateDiscountType = (e: string) => {
		changeKidsPolicyDiscountType({ discountType: e as KidsPolicyType, index });
	};

	const updateRatio = (input: string) => {
		changeKidsPolicyRatio({ ratio: Number(input), index });
	};

	const updateStaticPrice = (input: number | undefined) => {
		changeKidsPolicyStaticPrice({ staticPrice: input, index });
	};

	return (
		<QuestionCardItem canRemoveItem={canRemoveKidsPolicyItem({ index })} removeItem={remove(index)}>
			<div className="flex flex-col gap-y-8">
				<div className="flex flex-col gap-y-4">
					<FormLabel>年齢設定</FormLabel>
					<QuestionSectionWrapper>
						<p>{kidsPolicyKidsMaxAgeMinAge({ index })}歳から</p>
						<SelectField
							placeholder="選択"
							value={kidsPolicyItem.kidsMaxAge.value}
							options={kidsPolicyKidsMaxAgeOptions({ index })}
							handleValueChange={updateKidsMaxAge}
						/>
						<p>歳以下の子どもは</p>
						<SelectField
							placeholder="選択"
							value={kidsPolicyItem.kidsDiscountType.value}
							options={discountTypeOptions}
							handleValueChange={updateDiscountType}
						/>
					</QuestionSectionWrapper>
				</div>

			{showRatio && (
				<div className="flex flex-col gap-y-4">
					<FormLabel>{priceTypeLabel}</FormLabel>
					<QuestionSectionWrapper>
						<p>大人の</p>
						<SelectField
							placeholder="選択"
							value={kidsPolicyItem.ratio?.value}
							options={kidsPolicyRatioOptions}
							handleValueChange={updateRatio}
							errorMessage={kidsPolicyItem.ratio?.error}
						/>
						<p>の料金</p>
					</QuestionSectionWrapper>
				</div>
			)}

			{showStatic && (
				<div className="flex flex-col gap-y-4">
					<FormLabel>{priceTypeLabel}</FormLabel>
					<QuestionSectionWrapper>
						<p>子ども１人あたり</p>
						<InputNumberField
							value={kidsPolicyItem.static?.amount?.value}
							handleInput={updateStaticPrice}
							errorMessage={kidsPolicyItem.static?.amount?.error}
						/>
						<p>円（税込）</p>
					</QuestionSectionWrapper>
				</div>
			)}
			</div>
		</QuestionCardItem>
	);
};

QuestionKidsPolicyItem.displayName = "QuestionKidsPolicyItem";
